import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import './progress.css';

export interface FilePickerHandle {
  /** Forget the chosen file (after an upload). */
  clear: () => void;
}

export interface FilePickerProps {
  accept?: string;
  disabled?: boolean;
  /** Accessible name of the hidden input and the button text. */
  label: string;
  onFile: (file: File | null) => void;
}

/**
 * A file chooser in the panel's own language and style, as the previous app's
 * (D107): the native control prints "Choose File / No file chosen" in the
 * browser's language and cannot be styled, so a secondary button opens the
 * visually hidden input and the chosen file's name is shown beside it.
 */
export const FilePicker = forwardRef<FilePickerHandle, FilePickerProps>(function FilePicker({ accept, disabled, label, onFile }, ref) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [name, setName] = useState<string | null>(null);
  useImperativeHandle(ref, () => ({
    clear: () => {
      if (input.current) input.current.value = '';
      setName(null);
    },
  }));
  return (
    <span className="fs-file-picker">
      <input
        ref={input}
        type="file"
        className="fs-visually-hidden"
        tabIndex={-1}
        accept={accept}
        disabled={disabled}
        aria-label={label}
        onChange={(event) => {
          const file = event.target.files?.[0] ?? null;
          setName(file?.name ?? null);
          onFile(file);
        }}
      />
      <Button variant="secondary" disabled={disabled} onClick={() => input.current?.click()}>
        {label}
      </Button>
      <bdi className="fs-meta fs-muted fs-file-picker__name">{name ?? t('common.noFileChosen')}</bdi>
    </span>
  );
});
